/**
 * materialApi.js — modul-lokaler API-Client der Material-Schnellerfassung.
 * Nutzt das zentrale apiCall (JWT-Header, /api-Prefix) aus core/api/apiService.
 * (RULES: was nur 1 Modul braucht -> ins Modul.)
 */
import { apiCall } from 'core/api/apiService';

/** Katalog + Admin-Feldkonfiguration (gemerged). */
export const getFieldConfig = (user) =>
  apiCall('/material/field-config', 'GET', null, user);

/** Admin: Feld-Overrides speichern. items = [{ field_key, enabled, required }]. */
export const saveFieldConfig = (user, items) =>
  apiCall('/material/field-config', 'PUT', { items }, user);

/** Uebersicht angelegter Materialien (optional gefiltert). */
export const listMaterials = (user, { status, search } = {}) => {
  const qs = new URLSearchParams();
  if (status) qs.set('status', status);
  if (search) qs.set('search', search);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiCall(`/material${suffix}`, 'GET', null, user);
};

/** Einzelmaterial inkl. Texte (z. B. als Vorlage laden). */
export const getMaterial = (user, id) =>
  apiCall(`/material/${id}`, 'GET', null, user);

/** Material anlegen. payload = { fields, texts, status, reference_matnr }. */
export const createMaterial = (user, payload) =>
  apiCall('/material', 'POST', payload, user);

/** Material aendern. */
export const updateMaterial = (user, id, payload) =>
  apiCall(`/material/${id}`, 'PUT', payload, user);

/** In internen Katalog spiegeln. */
export const mirrorMaterial = (user, id, shopId = null) =>
  apiCall(`/material/${id}/mirror`, 'POST', shopId ? { shop_id: shopId } : {}, user);

/** SAP-Transfer: Vorschau des BAPI-Payloads (ohne Statuswechsel). */
export const previewTransfer = (user, id) =>
  apiCall(`/material/${id}/transfer-preview`, 'GET', null, user);

/** SAP-Transfer ausführen (Stub): Status -> übertragen. */
export const transferToSap = (user, id) =>
  apiCall(`/material/${id}/transfer`, 'POST', {}, user);
// Trockenlauf gegen SAP (BAPI-Aufruf + Rollback auf der ABAP-Seite): kein Statuswechsel.
export const checkTransfer = (user, id) =>
  apiCall(`/material/${id}/transfer-check`, 'POST', {}, user);
// Change-Pfad (uebertragene Materialien): Delta gegen den letzten SAP-Stand pruefen / senden.
export const checkUpdate = (user, id) =>
  apiCall(`/material/${id}/update-check`, 'POST', {}, user);
export const updateInSap = (user, id) =>
  apiCall(`/material/${id}/update`, 'POST', {}, user);

/** SAP-Materialstamm durchsuchen (Stub gegen Spiegel). */
export const searchSapMaterials = (user, q = '') =>
  apiCall(`/material/sap/search?q=${encodeURIComponent(q)}`, 'GET', null, user);

/** Ein SAP-Material laden (nur sichtbare Felder) — als Vorlage. */
export const getSapMaterial = (user, sapId) =>
  apiCall(`/material/sap/${sapId}`, 'GET', null, user);

/** Material loeschen. */
export const deleteMaterial = (user, id) =>
  apiCall(`/material/${id}`, 'DELETE', null, user);

// ---- Modul-Einstellungen (Dublettenpruefung) ----
export const setMaterialSettings = (user, patch) =>
  apiCall('/material/settings', 'PUT', patch, user);

// ---- Dublettenpruefung ----
export const checkDuplicates = (user, payload) =>
  apiCall('/material/check-duplicates', 'POST', payload, user);

// ---- Feldkonfigurations-Presets ----
export const getPresets = (user) =>
  apiCall('/material/presets', 'GET', null, user);
export const savePreset = (user, name, config) =>
  apiCall('/material/presets', 'POST', { name, config }, user);
export const deletePreset = (user, id) =>
  apiCall(`/material/presets/${id}`, 'DELETE', null, user);

// ---- PDF-Anhaenge ----
export const listAttachments = (user, materialId) =>
  apiCall(`/material/${materialId}/attachments`, 'GET', null, user);
export const deleteAttachment = (user, materialId, aid) =>
  apiCall(`/material/${materialId}/attachments/${aid}`, 'DELETE', null, user);

const authHeader = (user) =>
  (user?.token && !user.token.startsWith('mock-')) ? { Authorization: `Bearer ${user.token}` } : {};

/** PDF hochladen (multipart). */
export const uploadAttachment = async (user, materialId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`/api/material/${materialId}/attachments`, {
    method: 'POST', headers: authHeader(user), body: fd,
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => null);
    throw new Error(msg?.message || `HTTP ${res.status}`);
  }
  return res.json();
};

/** PDF als Blob laden (für Download/Anzeige mit Auth-Header). */
export const fetchAttachmentBlob = async (user, materialId, aid) => {
  const res = await fetch(`/api/material/${materialId}/attachments/${aid}`, { headers: authHeader(user) });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
};
